import React from 'react';
import { Trans } from 'react-i18next';
import { useAppSelector } from 'flow/hooks/useRedux';
import useAppContext from 'flow/hooks/useAppContext';
import {
    selectTicketActionEventNewValue,
    selectTicketActionEventPrevValue,
} from 'flow/selectors/TicketEventSelectors';
import { TicketKey } from 'flow/types/EntityKeys';
import { TicketActionEventKey } from 'flow/types/TicketEventData';
import ActionEventRow from './ActionEventRow';
import useActorTooltipLine from './useActorTooltipLine';

interface Props {
    ticketKey: TicketKey;
    eventKey: TicketActionEventKey;
}

const getStateI18nKey = (
    newValue: string | undefined,
    prevValue: string | undefined,
) => {
    if (newValue === 'closed') return 'tickets.state.closed';
    if (newValue === 'open' && prevValue === 'closed')
        return 'tickets.state.reopened';
    if (newValue === 'open' && !prevValue) return 'tickets.state.opened';
    return 'tickets.state.changed';
};

const ActionEventStateChanged = ({ ticketKey, eventKey }: Props) => {
    const { i18next } = useAppContext();

    const newValue = useAppSelector((state) =>
        selectTicketActionEventNewValue(state, ticketKey, eventKey),
    );
    const prevValue = useAppSelector((state) =>
        selectTicketActionEventPrevValue(state, ticketKey, eventKey),
    );
    const tooltipLabel = useActorTooltipLine(ticketKey, eventKey) ?? '';

    return (
        <ActionEventRow
            ticketKey={ticketKey}
            eventKey={eventKey}
            tooltipLabel={tooltipLabel}
        >
            <Trans
                i18n={i18next}
                i18nKey={getStateI18nKey(newValue, prevValue)}
                values={{ state: newValue }}
                components={[<strong key="state" />]}
            />
        </ActionEventRow>
    );
};

export default React.memo(ActionEventStateChanged);
